import { ClaimFormsServiceAPI } from '@API/data-services/claim-forms-service'
import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config/types'
import { RootState } from '@AppState/redux-store'
import { Modal } from '@Components/common/modal'
import { convertDateToIsoDate, getTodayAsUTC } from '@Components/util/utils'
import {
  ClaimCenterWithBookables,
  ClaimReservationWithTimeslot,
} from '@Generated/back-office-client/src'
import { Button, Flex, Text } from '@radix-ui/themes'
import { Typeahead } from '@Components/common/typeahead'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

type SelectOption = { value: string; label: string }

type LinkAppointmentModalProps = {
  claimFormId: string
  currentReservationId?: string | null
  onSuccess: () => void
  onClose: () => void
}

export const LinkAppointmentModal = ({
  claimFormId,
  currentReservationId,
  onSuccess,
  onClose,
}: LinkAppointmentModalProps) => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const queryClient = useQueryClient()

  const [selectedClaimCenterId, setSelectedClaimCenterId] = useState<string>('')
  const [selectedDate, setSelectedDate] = useState(convertDateToIsoDate(getTodayAsUTC()))
  const [selectedReservationId, setSelectedReservationId] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const { data: claimCenters } = useQuery({
    queryKey: [`${activeCustomerId}-claim-centers`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: () => ClaimReservationsServiceAPI.getClaimCenters(),
  })

  const { data: reservations, isLoading: isLoadingReservations } = useQuery({
    queryKey: ['link-appointment-reservations', selectedClaimCenterId, selectedDate],
    queryFn: () =>
      ClaimReservationsServiceAPI.getReservations(
        selectedClaimCenterId,
        selectedDate,
        selectedDate,
      ),
    enabled: !!selectedClaimCenterId && !!selectedDate,
  })

  const unlinkedReservations = useMemo(() => {
    if (!reservations) {
      return []
    }
    return reservations.filter(
      (r: ClaimReservationWithTimeslot) => !r.claimFormId && !r.claimReservationDetails.isCancelled,
    )
  }, [reservations])

  const claimCenterOptions = useMemo(() => {
    if (!claimCenters) {
      return []
    }
    return claimCenters.map((cc: ClaimCenterWithBookables) => ({
      value: cc.id,
      label: cc.name,
    }))
  }, [claimCenters])

  const claimantOptions = useMemo(() => {
    return unlinkedReservations.map((r: ClaimReservationWithTimeslot) => ({
      value: r.id,
      label: r.claimReservationDetails.name,
    }))
  }, [unlinkedReservations])

  const linkMutation = useMutation({
    mutationFn: async () => {
      setError(null)
      if (currentReservationId) {
        await ClaimFormsServiceAPI.relinkClaimForm(claimFormId, selectedReservationId)
      } else {
        await ClaimFormsServiceAPI.linkClaimForm(claimFormId, selectedReservationId)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claim-form'] })
      queryClient.invalidateQueries({ queryKey: ['claim-form-search'] })
      onSuccess()
    },
    onError: () => {
      setError('Failed to link appointment. Please try again.')
    },
  })

  return (
    <Modal
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      title="Link Appointment to Claim Form"
      description="Locate a player's appointment and attach it to this Claim Form."
      className="link-appointment-modal">
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="1">
          <Text size="5" weight="bold">
            Link Appointment to Claim Form
          </Text>
          <Text size="2" color="gray">
            Locate a player&apos;s appointment and attach it to this Claim Form.
          </Text>
        </Flex>

        <Flex direction="column" gap="2">
          <Text size="2" weight="bold">
            Select Claim Center
          </Text>
          <Typeahead<SelectOption>
            placeholder="Search claim centers..."
            isClearable
            options={claimCenterOptions}
            value={claimCenterOptions.find((o) => o.value === selectedClaimCenterId) ?? null}
            onChange={(option) => {
              setSelectedClaimCenterId(option?.value ?? '')
              setSelectedReservationId('')
            }}
          />
        </Flex>

        <Flex direction="column" gap="2">
          <Text size="2" weight="bold">
            Select Date
          </Text>
          <input
            type="date"
            className="rt-reset rt-SelectTrigger rt-r-size-2 rt-variant-surface"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value)
              setSelectedReservationId('')
            }}
          />
        </Flex>

        <Flex direction="column" gap="2">
          <Text size="2" weight="bold">
            Select Claimant
          </Text>
          <Typeahead<SelectOption>
            placeholder={
              isLoadingReservations
                ? 'Loading...'
                : claimantOptions.length === 0
                  ? 'No available appointments'
                  : 'Search claimants...'
            }
            isClearable
            isDisabled={!selectedClaimCenterId || !selectedDate || isLoadingReservations}
            isLoading={isLoadingReservations}
            options={claimantOptions}
            value={claimantOptions.find((o) => o.value === selectedReservationId) ?? null}
            onChange={(option) => setSelectedReservationId(option?.value ?? '')}
          />
        </Flex>

        {error ? (
          <Text color="red" size="2">
            {error}
          </Text>
        ) : null}

        <Button
          size="3"
          disabled={!selectedReservationId || linkMutation.isPending}
          onClick={() => linkMutation.mutate()}>
          {linkMutation.isPending ? 'Linking...' : 'Link'}
        </Button>
      </Flex>
    </Modal>
  )
}
